import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff, Check, X, ShieldCheck } from 'lucide-react';
import apiService from '../../services/api';
import {
  AdminPageLayout,
  Button,
  FormInput,
  FormSelect,
  FormTextArea,
  FormCheckbox,
  Section,
  Card,
  CardContent,
  Grid,
  Col,
  PhoneNumberInput
} from '../../components';
import {
  FormActionGroup,
  AttributeStack
} from '../../components/admin/patterns';
import PhotoUpload from '../../components/PhotoUpload';
import ProfileInfo from '../../components/ProfileInfo';
import { useFormValidation } from '../../hooks/useFormValidation';

const AddUser = () => {
  const navigate = useNavigate();
  const { branch_id } = useParams(); // Get branch_id from URL if present
  const [photoFile, setPhotoFile] = useState(null);

  const [availableRoles, setAvailableRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);

  // Role filtering based on context
  const BRANCH_ALLOWED_ROLES = ['branch_admin', 'coach', 'parent'];
  const CLUB_ADMIN_ALLOWED_ROLES = ['club_admin', 'branch_admin'];
  const isBranchContext = !!branch_id;

  const {
    formData,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      first_name: process.env.NODE_ENV === 'development' ? 'John' : '',
      last_name: process.env.NODE_ENV === 'development' ? 'Smith' : '',
      email: process.env.NODE_ENV === 'development' ? 'john.smith@example.com' : '',
      phone: process.env.NODE_ENV === 'development' ? '(555) 123-4567' : '',
      role: '',
      password: '',
      send_invitation: true,
      custom_message: process.env.NODE_ENV === 'development'
        ? 'Welcome to our soccer training program! Please check your email for login instructions.'
        : '',
      photo_url: '',
      qualifications: '',
      bio: '',
      enable_public_profile_display: true
    },
    validationSchema: (values) => {
      const newErrors = {};

      if (!values.first_name.trim()) {
        newErrors.first_name = 'First name is required';
      }

      if (!values.last_name.trim()) {
        newErrors.last_name = 'Last name is required';
      }

      if (!values.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(values.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!values.role) {
        newErrors.role = 'Role is required';
      }

      if (!values.send_invitation && !values.password.trim()) {
        newErrors.password = 'Password is required when not sending invitation';
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      try {
        const userData = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone || null,
          role: values.role,
          primary_role_id: values.role,
          primary_role: (
            availableRoles.find(r => r.id === values.role)?.slug
            || (availableRoles.find(r => r.id === values.role)?.name && availableRoles.find(r => r.id === values.role).name.trim().toLowerCase().replace(/\s+/g, '_'))
            || values.role
          ),
          password: values.password || undefined,
          send_invitation: values.send_invitation,
          custom_message: values.custom_message || null,
          qualifications: values.qualifications || null,
          bio: values.bio || null,
          enable_public_profile_display: values.enable_public_profile_display,
          // Add branch_id if in branch context (keep as string for API)
          ...(branch_id && { branch_id })
        };

        if (!userData.password) {
          delete userData.password;
        }

        const createResult = await apiService.createUserWithInvitation(userData);
        const newUserId = createResult.user_id;

        if (photoFile) {
          try {
            const authToken = localStorage.getItem('authToken');
            const photoFormData = new FormData();
            photoFormData.append('file', photoFile);

            const response = await fetch(`/api/admin/users/${newUserId}/photo`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`
              },
              body: photoFormData
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.warn('Photo upload failed:', errorData.detail || `Status ${response.status}`);
            }
          } catch (photoError) {
            console.warn('Photo upload error:', photoError);
          }
        }

        // Navigate to appropriate path based on context
        const targetPath = isBranchContext ? `/branch/${branch_id}/users` : '/admin/users';
        navigate(targetPath, {
          state: {
            message: `User ${values.first_name} ${values.last_name} has been created successfully!`
          }
        });
      } catch (err) {
        throw err;
      }
    }
  });

  useEffect(() => {
    loadAvailableRoles();
  }, [branch_id]); // Add branch_id as dependency to reload roles if context changes

  const loadAvailableRoles = async () => {
    try {
      const rolesResponse = await apiService.getClubRoles(false);
      let roles = rolesResponse.roles || rolesResponse || [];
      
      console.log('DEBUG AddUser - Raw roles from API:', roles);
      console.log('DEBUG AddUser - isBranchContext:', isBranchContext);
      
      // Filter roles based on context - always use club-level roles
      if (isBranchContext) {
        // Branch admin: can create branch_admin, coach, parent
        roles = roles.filter(role => {
          const roleName = (role.name || '').toLowerCase().replace(/\s+/g, '_');
          return BRANCH_ALLOWED_ROLES.includes(roleName);
        });
      } else {
        // Club admin: can create club_admin, branch_admin
        roles = roles.filter(role => {
          const roleName = (role.name || '').toLowerCase().replace(/\s+/g, '_');
          return CLUB_ADMIN_ALLOWED_ROLES.includes(roleName);
        });
      }
      
      console.log('DEBUG AddUser - Filtered roles:', roles);
      
      // If no roles match after filtering, use defaults
      if (roles.length === 0) {
        console.warn('No matching roles found after filtering. Using default roles for context:', isBranchContext ? 'branch' : 'club');
        if (isBranchContext) {
          roles = [
            { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' },
            { id: 'coach', name: 'Coach', description: 'Player evaluation and training management' },
            { id: 'parent', name: 'Parent', description: 'View child progress and manage waivers' }
          ];
        } else {
          roles = [
            { id: 'club_admin', name: 'Club Admin', description: 'Full club management access' },
            { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' }
          ];
        }
      }
      
      setAvailableRoles(roles);
    } catch (err) {
      console.error('Failed to load roles:', err);
      
      // Default roles based on context
      if (isBranchContext) {
        setAvailableRoles([
          { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' },
          { id: 'coach', name: 'Coach', description: 'Player evaluation and training management' },
          { id: 'parent', name: 'Parent', description: 'View child progress and manage waivers' }
        ]);
      } else {
        setAvailableRoles([
          { id: 'club_admin', name: 'Club Admin', description: 'Full club management access' },
          { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' }
        ]);
      }
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFieldValue('password', password);
    setShowPassword(true);
  };

  const handlePhotoSelect = (photoData) => {
    setPhotoFile(photoData.file);
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
  };

  const handleQualificationsChange = (value) => {
    setFieldValue('qualifications', value);
  };

  const handleBioChange = (value) => {
    setFieldValue('bio', value);
  };

  const handleSubmit = async (e) => {
    try {
      setError(null);
      await handleFormSubmit(e);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    }
  };

  const getRoleDescription = (roleId) => {
    const role = availableRoles.find(r => r.id === roleId);
    return role?.description || '';
  };

  return (
    <AdminPageLayout
      title="Add New User"
      subtitle="Add a new team member or admin"
      backPath={isBranchContext ? `/branch/${branch_id}/users` : '/admin/users'}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Section>
            <div style={{ padding: '1.25rem', background: 'var(--color-error-light)', border: '1px solid var(--color-error)', borderRadius: '12px', color: 'var(--color-error)', textAlign: 'center', fontWeight: 600 }}>
              Error: {error}
            </div>
          </Section>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
          {/* Identity & Contact */}
          <Section title="Personal Information">
            <Card level={1}>
              <CardContent>
                <Grid columns={12} gap="2.5rem">
                  <Col span={6}>
                    <FormInput
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.first_name ? errors.first_name : ''}
                      required
                      placeholder="John"
                    />
                  </Col>
                  <Col span={6}>
                    <FormInput
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.last_name ? errors.last_name : ''}
                      required
                      placeholder="Smith"
                    />
                  </Col>
                  <Col span={8}>
                    <FormInput
                      label="Email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.email ? errors.email : ''}
                      required
                      placeholder="john.smith@example.com"
                    />
                  </Col>
                  <Col span={4}>
                    <PhoneNumberInput
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.phone ? errors.phone : ''}
                      placeholder="(555) 123-4567"
                    />
                  </Col>
                </Grid>
              </CardContent>
            </Card>
          </Section>

          {/* Authorization & Credentials */}
          <Grid columns={2} gap="2.5rem">
            <Section title="Role">
              <Card level={1} height="100%">
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <FormSelect
                      label="Role"
                      name="role"
                      value={formData.role}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.role ? errors.role : ''}
                      required
                      options={[
                        { value: '', label: 'Choose a role...' },
                        ...availableRoles.map(role => ({ value: role.id, label: role.name }))
                      ]}
                    />
                    {formData.role && (
                      <div style={{ background: 'var(--surface-lvl2)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                        <AttributeStack
                          label="Permissions"
                          value={getRoleDescription(formData.role)}
                          variant="small"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Section>

            <Section title="Password & Invitation">
              <Card level={1} height="100%">
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <div style={{ background: 'var(--surface-lvl2)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                      <FormCheckbox
                        label="Send invitation email"
                        name="send_invitation"
                        checked={formData.send_invitation}
                        onChange={handleFieldChange}
                        helpText="The new user will receive their login details by email"
                      />
                    </div>

                    {!formData.send_invitation && (
                      <div style={{ background: 'var(--surface-lvl2)', padding: '2rem', borderRadius: '16px', border: '1px dashed var(--color-border)' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                          <div style={{ flex: 1 }}>
                            <FormInput
                              label="Password"
                              type={showPassword ? 'text' : 'password'}
                              name="password"
                              value={formData.password}
                              onChange={handleFieldChange}
                              onBlur={handleFieldBlur}
                              error={touched.password ? errors.password : ''}
                              required
                              placeholder="Min 8 characters"
                            />
                          </div>
                          <Button type="button" onClick={() => setShowPassword(!showPassword)} variant="outline" style={{ height: '42px', padding: '0 1rem' }}>
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </Button>
                          <Button type="button" onClick={generatePassword} variant="primary" style={{ height: '42px' }}>
                            GENERATE
                          </Button>
                        </div>
                      </div>
                    )}

                    {formData.send_invitation && (
                      <FormTextArea
                        label="Welcome message"
                        name="custom_message"
                        value={formData.custom_message}
                        onChange={handleFieldChange}
                        onBlur={handleFieldBlur}
                        rows={3}
                        placeholder="Optional: Add any additional information for the new user..."
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            </Section>
          </Grid>

          {/* Curated Background */}
          <Section title="Profile Information">
            <Card level={1}>
              <CardContent>
                <Grid columns={12} gap="3.5rem">
                  <Col span={4}>
                    <PhotoUpload
                      onPhotoSelect={handlePhotoSelect}
                      onPhotoRemove={handlePhotoRemove}
                      photoUrl={formData.photo_url}
                      label="Photo"
                      hint="JPG/PNG up to 5MB"
                    />
                  </Col>
                  <Col span={8}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                      <ProfileInfo
                        qualifications={formData.qualifications}
                        bio={formData.bio}
                        onQualificationsChange={handleQualificationsChange}
                        onBioChange={handleBioChange}
                        errors={errors}
                        touched={touched}
                        onBlur={handleFieldBlur}
                      />

                      <div style={{ background: 'var(--surface-lvl2)', padding: '1.75rem', borderRadius: '16px', border: '1px solid var(--color-border-light)' }}>
                        <FormCheckbox
                          label="Show profile publicly"
                          name="enable_public_profile_display"
                          checked={formData.enable_public_profile_display}
                          onChange={handleFieldChange}
                          helpText="Their profile will be visible publicly."
                        />
                      </div>
                    </div>
                  </Col>
                </Grid>
              </CardContent>
            </Card>
          </Section>

          <FormActionGroup
            onCancel={() => navigate(isBranchContext ? `/branch/${branch_id}/users` : '/admin/users')}
            onSave={handleSubmit}
            loading={isSubmitting}
            saveLabel="Create User"
            cancelLabel="Cancel"
          />
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default AddUser;
